import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputLabel,
    Select,
    MenuItem,
    FormControl,
    FormControlLabel,
    Typography,
    Checkbox,
    Divider,
    IconButton
} from '@mui/material';
import { useTheme } from '@mui/system';
import { Add, Delete } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import ApiHelper from 'utils/apiHelper';
import { validateNumber } from 'utils/commonHelpers';

const EditRuleDialog = ({ onClose, onCloseCancel, ruleType, commonData, ruleData, ruleStatementValues, ruleStatementsData }) => {
    const theme = useTheme();
    const [ruleName, setRuleName] = useState(ruleData.name);
    const [ruleDescription, setRuleDescription] = useState(ruleData.description);
    const [enabled, setEnabled] = useState(ruleData.enabled);
    const [groupId, setGroupId] = useState(ruleData.groupid?.id);
    const [policyId, setPolicyId] = useState(ruleData.policyid?.policyid);
    const [orderValue, setOrderValue] = useState(ruleData.order);
    const [ruleStatements, setRuleStatements] = useState(ruleStatementsData);
    const [deletedRuleStatements, setDeletedRuleStatements] = useState([]);
    const [addedRuleStatements, setAddedRuleStatements] = useState([]);
    const [disableAddClause, setDisableAddClause] = useState(true);
    const operatorLookupsData = ruleType === 'group' ? commonData.group.operator_lookups : commonData.policy.operator_lookups;
    const [groupAttributeId, setGroupAttributeId] = useState();
    const [deviceTypeAttributeId, setDeviceTypeAttributeId] = useState();

    useEffect(() => {
        operatorLookupsData.map((operatorLookupData) => {
            if (operatorLookupData.attrib.attribname === 'groupid') {
                setGroupAttributeId(operatorLookupData.attrib.attribid);
            }
            if (operatorLookupData.attrib.attribname === 'devtype') {
                setDeviceTypeAttributeId(operatorLookupData.attrib.attribid);
            }
        });
        ruleStatements.map((ruleStatementData) => {
            if (ruleStatementData['attribute'] !== '' && ruleStatementData['operator'] !== '' && ruleStatementData['value'] !== '') {
                setDisableAddClause(false);
            } else {
                setDisableAddClause(true);
            }
        });
    }, [ruleStatements, operatorLookupsData]);

    const handleAddClause = () => {
        const tmp = JSON.parse(JSON.stringify(ruleStatements));
        for (let i = 0; i < tmp.length; i++) {
            tmp[i]['disabledConnector'] = false;
        }
        const newRuleStatementId = ruleStatements[0].id * 10 + ruleStatements.length + 1;
        tmp.push({
            id: newRuleStatementId,
            attribute: '',
            negative: false,
            operator: '',
            value: '',
            connector: '1',
            disabledConnector: true,
            operatorOptions: commonData.operators,
            valueTextField: true,
            valueOptions: []
        });
        setRuleStatements(tmp);
        const newStatements = JSON.parse(JSON.stringify(addedRuleStatements));
        const index = newStatements.findIndex((id) => id === newRuleStatementId);
        if (index === -1) {
            newStatements.push(newRuleStatementId);
            setAddedRuleStatements(newStatements);
        }
    };

    const handleDeleteRule = (key) => {
        const tmp = JSON.parse(JSON.stringify(ruleStatements));
        const index = tmp.findIndex((rule) => rule.id === key);
        tmp.splice(index, 1);
        tmp[tmp.length - 1]['disabledConnector'] = true;
        setRuleStatements(tmp);
        const addedStatements = JSON.parse(JSON.stringify(addedRuleStatements));
        const addedStatementIndex = addedStatements.findIndex((id) => id === key);
        if (addedStatementIndex === -1) {
            let deletedStatements = JSON.parse(JSON.stringify(deletedRuleStatements));
            const deletedStatementIndex = deletedStatements.findIndex((id) => id === key);
            if (deletedStatementIndex === -1) {
                deletedStatements.push(key);
                setDeletedRuleStatements(deletedStatements);
            }
        } else {
            addedStatements.splice(addedStatementIndex, 1);
            setAddedRuleStatements(addedStatements);
        }
    };

    const handleChangeRuleData = (e, ruleId) => {
        const tmp = JSON.parse(JSON.stringify(ruleStatements));
        const selectedRule = tmp.filter((rule) => rule.id === ruleId);
        if (e.target.name === 'negative') {
            selectedRule[0][e.target.name] = e.target.checked;
        } else {
            selectedRule[0][e.target.name] = e.target.value;
            const operatorLookup = operatorLookupsData.some((item) => item.attrib.attribid === selectedRule[0]['attribute']);
            if (operatorLookup) {
                selectedRule[0]['operatorOptions'] = [operatorLookupsData[0].theoper];
                if (selectedRule[0]['attribute'] === groupAttributeId) selectedRule[0]['valueOptions'] = ruleStatementValues.group_values;
                else if (selectedRule[0]['attribute'] === deviceTypeAttributeId)
                    selectedRule[0]['valueOptions'] = ruleStatementValues.device_type_values;
                else selectedRule[0]['valueOptions'] = [{ name: 'Yes' }, { name: 'No' }];
                selectedRule[0]['valueTextField'] = false;
            } else {
                selectedRule[0]['valueTextField'] = true;
                selectedRule[0]['operatorOptions'] = commonData.operators;
            }
        }
        setRuleStatements(tmp);
    };

    const handleSubmit = () => {
        if (ruleName === '') {
            toast.error('The rule name is required');
            return true;
        }
        if ((ruleType === 'group' && groupId === '') || (ruleType === 'policy' && policyId === '')) {
            toast.error(`Please select the ${ruleType} value`);
            return true;
        }
        if (orderValue === '') {
            toast.error('Order value is required and it should be an integer');
            return true;
        } else {
            if (validateNumber(orderValue, 'integer') !== true) {
                toast.error('Order value should be an integer');
                return true;
            }
        }

        ApiHelper.put(`/api/automation/rules/${ruleData.ruleid}/`, {
            rule_name: ruleName,
            rule_description: ruleDescription,
            rule_type: ruleType,
            enabled: enabled,
            order_value: parseInt(orderValue),
            group_id: ruleType === 'group' ? groupId : '',
            policy_id: ruleType === 'policy' ? policyId : '',
            rule_statements: ruleStatements,
            deleted_rule_statements: deletedRuleStatements
        })
            .then(() => {
                toast.success('Successfully updated.');
                onClose();
            })
            .catch((err) => {
                toast.error(`Failed! ${err.response.data.message}`);
            });
    };

    if (ruleStatements.length > 0) {
        return (
            <Dialog open={true} onClose={onCloseCancel}>
                <DialogTitle sx={{ fontSize: '16px' }}>Edit Rule</DialogTitle>
                <DialogContent sx={{ minWidth: '900px' }}>
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <Grid container spacing={1}>
                            <Grid item xs={6}>
                                <TextField
                                    sx={{ width: '100%' }}
                                    label="Rule Name"
                                    value={ruleName}
                                    onChange={(e) => setRuleName(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    sx={{ width: '100%' }}
                                    label="Rule Description"
                                    value={ruleDescription}
                                    onChange={(e) => setRuleDescription(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                {ruleType === 'group' ? (
                                    <FormControl sx={{ width: '100%', mt: 1 }}>
                                        <InputLabel>Group</InputLabel>
                                        <Select label="Group" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                                            {commonData.group.groups_data.map((groupData, key) => (
                                                <MenuItem key={key} value={groupData.id}>
                                                    {groupData.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <FormControl sx={{ width: '100%', mt: 1 }}>
                                        <InputLabel>Policy</InputLabel>
                                        <Select label="Policy" value={policyId} onChange={(e) => setPolicyId(e.target.value)}>
                                            {commonData.policy.policies_data.map((policyData, key) => (
                                                <MenuItem key={key} value={policyData.policyid}>
                                                    {policyData.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Grid>
                            <Grid item xs={3}>
                                <FormControl sx={{ width: '100%', mt: 1 }}>
                                    <InputLabel>Order</InputLabel>
                                    <Select label="Order" value={orderValue} onChange={(e) => setOrderValue(e.target.value)}>
                                        {ruleType === 'group' &&
                                            commonData.group.editable_order_values.map((value, key) => (
                                                <MenuItem key={key} value={value}>
                                                    {value}
                                                </MenuItem>
                                            ))}
                                        {ruleType === 'policy' &&
                                            commonData.policy.editable_order_values.map((value, key) => (
                                                <MenuItem key={key} value={value}>
                                                    {value}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={3} sx={{ mt: 1 }}>
                                <FormControlLabel
                                    value="end"
                                    sx={{ float: 'right' }}
                                    control={<Checkbox checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
                                    label="Enabled"
                                    labelPlacement="end"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    <Divider sx={{ border: `1px solid ${theme.palette.primary.main}` }} />
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <Grid container spacing={1}>
                            {ruleStatements.map((ruleStatement, key) => (
                                <React.Fragment key={key}>
                                    <Grid item xs={1}>
                                        {key === 0 ? (
                                            <Typography sx={{ padding: 2, fontSize: '16px' }}>IF</Typography>
                                        ) : (
                                            <IconButton
                                                size="medium"
                                                sx={{
                                                    float: 'right'
                                                }}
                                                color="error"
                                                onClick={() => handleDeleteRule(ruleStatement.id)}
                                            >
                                                <Delete fontSize="medium" />
                                            </IconButton>
                                        )}
                                    </Grid>
                                    <Grid item xs={3}>
                                        <FormControl sx={{ width: '100%' }}>
                                            <InputLabel>Attribute</InputLabel>
                                            <Select
                                                label="Attribute"
                                                name="attribute"
                                                value={ruleStatement['attribute']}
                                                onChange={(e) => handleChangeRuleData(e, ruleStatement.id)}
                                            >
                                                {ruleType === 'group' &&
                                                    commonData.group.attributes.map((attributeData, key) => (
                                                        <MenuItem key={key} value={attributeData.attribid}>
                                                            {attributeData.attrib_label}
                                                        </MenuItem>
                                                    ))}
                                                {ruleType === 'policy' &&
                                                    commonData.policy.attributes.map((attributeData, key) => (
                                                        <MenuItem key={key} value={attributeData.attribid}>
                                                            {attributeData.attrib_label}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={1}>
                                        <FormControlLabel
                                            value="top"
                                            control={
                                                <Checkbox
                                                    name="negative"
                                                    value={ruleStatement['negative']}
                                                    checked={ruleStatement['negative']}
                                                    onChange={(e) => handleChangeRuleData(e, ruleStatement.id)}
                                                />
                                            }
                                            label="NOT"
                                            labelPlacement="top"
                                        />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <FormControl sx={{ width: '100%' }}>
                                            <InputLabel>Operator</InputLabel>
                                            <Select
                                                label="Operator"
                                                name="operator"
                                                value={ruleStatement['operator']}
                                                onChange={(e) => handleChangeRuleData(e, ruleStatement.id)}
                                            >
                                                {ruleStatement['operatorOptions'].map((operatorData, key) => (
                                                    <MenuItem key={key} value={operatorData.operatorid}>
                                                        {operatorData.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={3}>
                                        {ruleStatement['valueTextField'] === false ? (
                                            <FormControl sx={{ width: '100%' }}>
                                                <InputLabel>Value</InputLabel>
                                                <Select
                                                    label="Value"
                                                    name="value"
                                                    value={ruleStatement['value']}
                                                    onChange={(e) => handleChangeRuleData(e, ruleStatement.id)}
                                                >
                                                    {ruleStatement['valueOptions'].map((valueOption, key) => (
                                                        <MenuItem
                                                            key={key}
                                                            value={valueOption.name ? valueOption.name : valueOption.devtype}
                                                        >
                                                            {valueOption.name ? valueOption.name : valueOption.devtype}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        ) : (
                                            <TextField
                                                sx={{ width: '100%' }}
                                                label="Value"
                                                name="value"
                                                value={ruleStatement['value']}
                                                onChange={(e) => handleChangeRuleData(e, ruleStatement.id)}
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={1}>
                                        <Select
                                            name="connector"
                                            value={ruleStatement['connector']}
                                            disabled={ruleStatement['disabledConnector']}
                                            onChange={(e) => handleChangeRuleData(e, ruleStatement.id)}
                                        >
                                            {commonData.connectors.map((connectorData, key) => (
                                                <MenuItem key={key} value={connectorData.connectorid}>
                                                    {connectorData.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>
                                </React.Fragment>
                            ))}
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" startIcon={<Add />} onClick={handleAddClause} disabled={disableAddClause}>
                        Add Clause
                    </Button>
                    <Button onClick={onCloseCancel}>Cancel</Button>
                    <Button onClick={handleSubmit} disabled={disableAddClause}>
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
        );
    } else {
        return null;
    }
};

EditRuleDialog.propTypes = {
    onClose: PropTypes.func,
    onCloseCancel: PropTypes.func,
    ruleType: PropTypes.string,
    commonData: PropTypes.object,
    ruleData: PropTypes.object,
    ruleStatementValues: PropTypes.object,
    ruleStatementsData: PropTypes.array
};

export default EditRuleDialog;
